import React, { useEffect, useState } from 'react'
import { Col, Modal, Row } from 'react-bootstrap'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import axios from 'axios'
import { ResetDefaultPassword } from '../../../../modules/auth/core/_requests'
import clsx from 'clsx'
import { useAuth } from '../../../../modules/auth/core/Auth'

type Props = {
  show: boolean
  handleClose: () => void
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^*])(?=.{8,})/
const ALLOWED_SPECIAL_CHARS = '! @ # $ % ^ *'

const ResetSchema = Yup.object().shape({
  Password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      PASSWORD_REGEX,
      `Password must contain at least 8 characters, including an uppercase letter (A–Z), a lowercase letter (a–z), a number (0–9), and one allowed special character (${ALLOWED_SPECIAL_CHARS})`
    ),
  ConfirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('Password')], 'Passwords must match'),
})

const ResetPasswordModal: React.FC<Props> = ({ show, handleClose }) => {
  useEffect(() => {
    initMap()
  }, [])

  //const [location, setLocation] = useState(data.location)
  const dissmissLocation = () => {
    //setLocation(data.location)
    handleClose()
  }

  const initMap = () => { }

  const initialValues = {
    Password: '',
    ConfirmPassword: ''
  }
  const {currentUser, logout} = useAuth()
  const username = currentUser?.UserName!
  const sessioncode = currentUser?.sessioncode
  const [loading, setLoading] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const formik = useFormik({
    initialValues,
    validationSchema: ResetSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      if (!username || !sessioncode) {
        setHasErrors(true)
        setSubmitting(false)
        setStatus('Session expired. Please log in again.')
        return
      }

      setLoading(true)
      setHasErrors(undefined)
      setTimeout(async () => {
        try {
          const { data } = await ResetDefaultPassword(username, values.ConfirmPassword, sessioncode)
          if (data.status === 'success') {
            setHasErrors(false)
            setLoading(false)
            setStatus(data.msg)
            window.location.reload();
          } else {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus(data.msg || 'Password reset failed')
          }
        } catch (error) {
          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)

          if (axios.isAxiosError(error) && error.response?.status === 401) {
            setStatus('Session data does not match this user. Please sign in again.')
            logout()
            return
          }

          setStatus('Error')
        }
      }, 1000)
    },
  })

  return (
    <Modal
      className='modal fade'
      id='kt_modal_reset'
      data-backdrop='static'
      data-keyboard='false'
      tabIndex={-1}
      role='model'
      show={show}
      dialogClassName=' modal-dialog-scrollable'
      aria-hidden='true'
      onHide={dissmissLocation}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Change Password</h5>

          
        </div>
        <div className='modal-body'>
          <Row>
            <Col xs={12} md={12}>
              <form
                className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
                noValidate
                id='kt_default_Password_Change_form'
                onSubmit={formik.handleSubmit}
              >

                {/* begin::Password policy */}
                <div className='mb-7 p-4 rounded' style={{ background: '#f9f9f9', border: '1px solid #e4e6ef' }}>
                  <p className='fw-bold text-dark fs-7 mb-2'>Password must meet all of the following requirements:</p>
                  <ul className='mb-0 ps-4 fs-7 text-gray-700' style={{ lineHeight: '1.8' }}>
                    <li>Minimum <strong>8 characters</strong></li>
                    <li>At least one <strong>uppercase</strong> letter (A–Z)</li>
                    <li>At least one <strong>lowercase</strong> letter (a–z)</li>
                    <li>At least one <strong>number</strong> (0–9)</li>
                    <li>At least one <strong>special character</strong>: <code>{ALLOWED_SPECIAL_CHARS}</code></li>
                  </ul>
                </div>
                {/* end::Password policy */}

                {/* begin::Form group Password */}
                <div className='fv-row mb-7'>
                  <label className='form-label fw-bolder text-dark fs-6'>New Password</label>
                  <input
                    placeholder={`Min. 8 chars · A–Z · a–z · 0–9 · special: ${ALLOWED_SPECIAL_CHARS}`}
                    type='password'
                    autoComplete='off'
                    {...formik.getFieldProps('Password')}
                    className={clsx(
                      'form-control form-control-solid',
                      { 'is-invalid': formik.touched.Password && formik.errors.Password },
                      { 'is-valid': formik.touched.Password && !formik.errors.Password }
                    )}
                  />
                  {formik.touched.Password && formik.errors.Password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Password}</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* end::Form group */}

                {/* begin::Form group Confirm Password  */}
                <div className='fv-row mb-7'>
                  <label className='form-label fw-bolder text-dark fs-6'>Confirm Password</label>
                  <input
                    placeholder='Re-enter your new password'
                    type='password'
                    autoComplete='off'
                    {...formik.getFieldProps('ConfirmPassword')}
                    className={clsx(
                      'form-control form-control-solid',
                      { 'is-invalid': formik.touched.ConfirmPassword && formik.errors.ConfirmPassword },
                      { 'is-valid': formik.touched.ConfirmPassword && !formik.errors.ConfirmPassword }
                    )}
                  />
                  {formik.touched.ConfirmPassword && formik.errors.ConfirmPassword && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ConfirmPassword}</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* end::Form group */}
                {/* begin::Form group */}
                <div className='text-center'>
                  <button
                    type='submit'
                    id='kt_sign_up_submit'
                    className='btn btn-lg btn-primary w-100 mb-5'
                    disabled={formik.isSubmitting || !formik.isValid }
                  >
                    {!loading && <span className='indicator-label'>Update Password</span>}
                    {loading && (
                      <span className='indicator-progress' style={{ display: 'block' }}>
                        Please wait...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
                {/* end::Form group */}
              </form>
            </Col>
          </Row>
        </div>

      </div>
    </Modal>
  )
}

export { ResetPasswordModal }
